import { type ChangeEvent, type FocusEvent, useState } from "react";
import { useWatch as useRHFWatch, type Control, useForm as useRHFForm } from "react-hook-form";
import { useFormContext, useAvailableFormInstance, registerFormInstance } from "./context";
import type { FieldPath, FieldValues, UseFormReturn, UseFormProps } from "react-hook-form";
import type { FormInstance } from "./Form";

type FormEvent<T = Element> = ChangeEvent<T> | FocusEvent<T>;
type EventHandler<T = Element> = (event: FormEvent<T>) => void;

// 自定义 useForm hook 选项
export interface CustomUseFormProps<TFieldValues extends FieldValues = FieldValues> extends UseFormProps<TFieldValues> {
    // 是否注册为全局默认实例
    registerAsGlobal?: boolean;
    // 注册的键名（如果不提供则作为默认实例）
    globalKey?: string;
}

// 将 UseFormReturn 转换为 FormInstance
export function createFormInstance<TFieldValues extends FieldValues = FieldValues>(
    formReturn: UseFormReturn<TFieldValues>
): FormInstance<TFieldValues> {
    return {
        ...formReturn,
        // 基础方法
        getFieldValue: (name: FieldPath<TFieldValues>) => formReturn.getValues(name),
        getFieldsValue: () => formReturn.getValues(),
        setFieldValue: (name: FieldPath<TFieldValues>, value: any) => formReturn.setValue(name, value),
        setFieldsValue: (values: Partial<TFieldValues>) => {
            Object.entries(values).forEach(([key, value]) => {
                formReturn.setValue(key as FieldPath<TFieldValues>, value);
            });
        },
        resetFields: () => formReturn.reset(),
        
        // 验证方法
        validateFields: async () => {
            const result = await formReturn.trigger();
            return result;
        },
        setFieldError: (name: FieldPath<TFieldValues>, error: any) => formReturn.setError(name, error),
        clearFieldErrors: (names?: FieldPath<TFieldValues>[]) => formReturn.clearErrors(names),
    };
}

// 自定义的 useForm hook，封装 react-hook-form 实现
export function useForm<TFieldValues extends FieldValues = FieldValues>(
    options?: CustomUseFormProps<TFieldValues>
): FormInstance<TFieldValues> {
    const { registerAsGlobal = false, globalKey, ...formOptions } = options || {};
    
    // 使用 react-hook-form 的 useForm
    const formReturn = useRHFForm<TFieldValues>(formOptions);
    
    // 转换为 FormInstance
    const formInstance = createFormInstance(formReturn);
    
    // 如果需要注册为全局实例
    if (registerAsGlobal) {
        registerFormInstance(formInstance, globalKey);
    }
    
    return formInstance;
}

export const useFieldUpdate = <T = Element>(name: string) => {
    const { setValue, formState: { errors }, getValues } = useFormContext();
    const [count, setCount] = useState(0);

    return {
        isInvalid: () => !!errors[name] || (count > 0 && !getValues(name)),
        handle: (handler: EventHandler<T>) => (event: FormEvent<T>) => {
            setCount(prev => prev + 1);
            handler(event);
        },
        clear: () => {
            setValue(name, '');
        }
    }
}

// 导出useWatch hook，用于在组件中监听表单字段变化
export function useWatch<TFieldValues extends FieldValues = FieldValues>(
    name: FieldPath<TFieldValues>,
    control?: Control<TFieldValues>
): any;
export function useWatch<TFieldValues extends FieldValues = FieldValues>(
    name: FieldPath<TFieldValues>[],
    control?: Control<TFieldValues>
): any[];
export function useWatch<TFieldValues extends FieldValues = FieldValues>(
    name: FieldPath<TFieldValues> | FieldPath<TFieldValues>[],
    control?: Control<TFieldValues>
): any {
    // 获取 control：优先使用传入的 control，其次使用可用的 FormInstance
    let resolvedControl: Control<TFieldValues>;
    
    if (control) {
        resolvedControl = control;
    } else {
        const availableInstance = useAvailableFormInstance<TFieldValues>();
        if (!availableInstance) {
            throw new Error('useWatch requires either a control parameter or must be used within a FormProvider or with a registered global form instance');
        }
        resolvedControl = availableInstance.control;
    }

    if (Array.isArray(name)) {
        return useRHFWatch({
            control: resolvedControl,
            name: name as readonly FieldPath<TFieldValues>[]
        });
    } else {
        return useRHFWatch({
            control: resolvedControl,
            name: name as FieldPath<TFieldValues>
        });
    }
}

// 导出一个更简单的字段值监听hook
export function useFieldValue<TFieldValues extends FieldValues = FieldValues>(
    name: FieldPath<TFieldValues>,
    control?: Control<TFieldValues>
): any {
    // 获取 control：优先使用传入的 control，其次使用可用的 FormInstance
    let resolvedControl: Control<TFieldValues>;
    
    if (control) {
        resolvedControl = control;
    } else {
        const availableInstance = useAvailableFormInstance<TFieldValues>();
        if (!availableInstance) {
            throw new Error('useFieldValue requires either a control parameter or must be used within a FormProvider or with a registered global form instance');
        }
        resolvedControl = availableInstance.control;
    }
    
    return useRHFWatch({ name, control: resolvedControl });
}

// 导出一个用于监听多个字段并返回是否都有值的hook
export function useFieldsWatcher<TFieldValues extends FieldValues = FieldValues>(
    names: FieldPath<TFieldValues>[],
    control?: Control<TFieldValues>
): {
    values: any[];
    allFilled: boolean;
    anyFilled: boolean;
} {
    // 获取 control：优先使用传入的 control，其次使用可用的 FormInstance
    let resolvedControl: Control<TFieldValues>;
    
    if (control) {
        resolvedControl = control;
    } else {
        const availableInstance = useAvailableFormInstance<TFieldValues>();
        if (!availableInstance) {
            throw new Error('useFieldsWatcher requires either a control parameter or must be used within a FormProvider or with a registered global form instance');
        }
        resolvedControl = availableInstance.control;
    }

    const values = useRHFWatch({ name: names, control: resolvedControl });

    return {
        values,
        allFilled: Array.isArray(values) ? values.every(Boolean) : false,
        anyFilled: Array.isArray(values) ? values.some(Boolean) : Boolean(values),
    };
}
